import type { Address } from 'viem';
import type { EncodedTx, SubmissionResult } from '../types.js';
import type { ClientMode, OstiumClientConfig } from '../config.js';
import { SelfSubmissionStrategy } from './self.js';
import { GaslessSubmissionStrategy } from './gasless.js';

export interface SubmissionStrategy {
  /**
   * The address that will appear as msg.sender on-chain.
   * - Self mode:    EOA address derived from privateKey.
   * - Gasless mode: Safe smart-account address derived from privateKey.
   */
  readonly effectiveSender: Address;

  submit(encoded: EncodedTx): Promise<SubmissionResult>;
}

/**
 * Factory — synchronous for Self mode, but for Gasless mode the returned
 * strategy's effectiveSender is pre-resolved via GaslessSubmissionStrategy.create().
 * Call createSubmitter() which handles both cases uniformly.
 */
export async function createSubmitter(
  config: OstiumClientConfig,
  testnet: boolean,
  mode: ClientMode,
): Promise<SubmissionStrategy> {
  if (mode === 'self-gasless' || mode === 'delegated-gasless') {
    return GaslessSubmissionStrategy.create(config, testnet);
  }
  if (!config.rpcUrl) {
    throw new Error('rpcUrl is required for self-submission mode');
  }
  return new SelfSubmissionStrategy(config, testnet);
}
